import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { X, Search, Loader2, Link2, FileText, User, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useCatalog } from "@/hooks/useCatalog";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";

export default function CaseForm({ isOpen, onClose, onSuccess, preselectedContactId }) {
  // Creation mode: 'deal' = associate with existing deal, 'standalone' = create without deal
  const [creationMode, setCreationMode] = useState('deal');
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // For deal mode - now searches by contact first
  const [contactSearchForDeal, setContactSearchForDeal] = useState("");
  const [contactsForDeal, setContactsForDeal] = useState([]);
  const [isLoadingContactsForDeal, setIsLoadingContactsForDeal] = useState(false);
  const [selectedContactForDeal, setSelectedContactForDeal] = useState(null);
  const [contactDeals, setContactDeals] = useState([]);
  const [isLoadingContactDeals, setIsLoadingContactDeals] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealItems, setDealItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // For standalone mode
  const { items: catalogItems, loading: loadingCatalog } = useCatalog();
  const [selectedCatalogId, setSelectedCatalogId] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Case configuration
  const [status, setStatus] = useState("preparation");
  const [priority, setPriority] = useState("medium");

  // Filter catalog to only show case types
  const caseTypes = useMemo(() => {
    return catalogItems.filter(item => item.type === 'case' || item.type === 'service');
  }, [catalogItems]);

  // Selected catalog item details
  const selectedCatalog = useMemo(() => {
    return caseTypes.find(c => c.id === selectedCatalogId);
  }, [caseTypes, selectedCatalogId]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      if (preselectedContactId) {
        loadPreselectedContact(preselectedContactId);
      }
    }
  }, [isOpen, preselectedContactId]);

  // Search contacts for deal mode when query changes
  useEffect(() => {
    if (contactSearchForDeal.length >= 1) {
      searchContactsForDeal(contactSearchForDeal);
    } else {
      setContactsForDeal([]);
    }
  }, [contactSearchForDeal]);

  // Load deals when contact is selected for deal mode
  useEffect(() => {
    if (selectedContactForDeal) {
      loadContactDeals(selectedContactForDeal.id);
    } else {
      setContactDeals([]);
      setSelectedDeal(null);
      setDealItems([]);
      setSelectedItem(null);
    }
  }, [selectedContactForDeal]);

  // Search contacts when query changes
  useEffect(() => {
    if (contactSearch.length >= 1) {
      searchContacts(contactSearch);
    } else {
      setContacts([]);
    }
  }, [contactSearch]);

  // Load items when deal is selected
  useEffect(() => {
    if (selectedDeal) {
      loadDealItems(selectedDeal.id);
    } else {
      setDealItems([]);
      setSelectedItem(null);
    }
  }, [selectedDeal]);

  const loadPreselectedContact = async (contactId) => {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_email, primary_phone')
        .eq('id', contactId)
        .single();

      if (error) throw error;
      if (data) {
        setSelectedContact(data);
        setCreationMode('standalone');
      }
    } catch (error) {
      console.error('Error loading preselected contact:', error);
    }
  };

  const searchContactsForDeal = async (query) => {
    setIsLoadingContactsForDeal(true);
    try {
      const words = query.trim().split(/\s+/).filter(w => w.length > 0);
      
      let supaQuery = supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_email, primary_phone')
        .order('first_name')
        .limit(20);

      if (words.length === 1) {
        supaQuery = supaQuery.or(
          `first_name.ilike.${words[0]}%,last_name.ilike.${words[0]}%,primary_email.ilike.${words[0]}%,primary_phone.ilike.${words[0]}%`
        );
      } else {
        supaQuery = supaQuery.or(
          `and(first_name.ilike.${words[0]}%,last_name.ilike.${words[1]}%),first_name.ilike.${words.join(' ')}%,last_name.ilike.${words.join(' ')}%`
        );
      }

      const { data, error } = await supaQuery;

      if (error) throw error;
      setContactsForDeal(data || []);
    } catch (error) {
      console.error('Error searching contacts for deal:', error);
      toast.error('Erro ao buscar contatos');
    } finally {
      setIsLoadingContactsForDeal(false);
    }
  };

  const loadContactDeals = async (contactId) => {
    setIsLoadingContactDeals(true);
    try {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          id,
          name,
          stage,
          total_amount,
          created_at,
          contact:contact_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContactDeals(data || []);
    } catch (error) {
      console.error('Error loading contact deals:', error);
      toast.error('Erro ao carregar negociações');
    } finally {
      setIsLoadingContactDeals(false);
    }
  };

  const loadDealItems = async (dealId) => {
    try {
      const { data, error } = await supabase
        .from('items')
        .select(`
          id,
          status,
          unit_price,
          catalog:catalog_id (
            id,
            name,
            type
          )
        `)
        .eq('deal_id', dealId);

      if (error) throw error;
      // Filter to only show items with catalog type 'case'
      const caseItems = (data || []).filter(item => item.catalog?.type === 'case' || item.catalog?.type === 'service');
      setDealItems(caseItems);
    } catch (error) {
      console.error('Error loading deal items:', error);
      toast.error('Erro ao carregar itens do deal');
    }
  };

  const searchContacts = async (query) => {
    setIsLoadingContacts(true);
    try {
      const words = query.trim().split(/\s+/).filter(w => w.length > 0);
      
      let supaQuery = supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_email, primary_phone')
        .order('first_name')
        .limit(20);

      if (words.length === 1) {
        supaQuery = supaQuery.or(
          `first_name.ilike.${words[0]}%,last_name.ilike.${words[0]}%,primary_email.ilike.${words[0]}%,primary_phone.ilike.${words[0]}%`
        );
      } else {
        supaQuery = supaQuery.or(
          `and(first_name.ilike.${words[0]}%,last_name.ilike.${words[1]}%),first_name.ilike.${words.join(' ')}%,last_name.ilike.${words.join(' ')}%`
        );
      }

      const { data, error } = await supaQuery;

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error('Error searching contacts:', error);
      toast.error('Erro ao buscar contatos');
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const handleSubmit = async () => {
    setIsProcessing(true);

    try {
      if (creationMode === 'deal') {
        // Mode A: Associate with existing deal/item
        if (!selectedItem) {
          toast.error('Selecione um item do deal');
          setIsProcessing(false);
          return;
        }

        // Check if case already exists for this item
        const { data: existingCase } = await supabase
          .from('cases')
          .select('id')
          .eq('item_id', selectedItem.id)
          .maybeSingle();

        if (existingCase) {
          toast.error('Já existe um case para este item');
          setIsProcessing(false);
          return;
        }

        // Create case linked to item
        const { data: newCase, error: caseError } = await supabase
          .from('cases')
          .insert({
            item_id: selectedItem.id,
            status: status,
            priority: priority,
            visa_type: selectedItem.catalog?.name || 'Unknown'
          })
          .select()
          .single();

        if (caseError) throw caseError;

        // Sync all item_contacts to case_contacts
        if (selectedItem?.id) {
          const { data: itemContacts, error: icError } = await supabase
            .from('item_contacts')
            .select('*')
            .eq('item_id', selectedItem.id);

          if (!icError && itemContacts && itemContacts.length > 0) {
            for (const ic of itemContacts) {
              await supabase.from('case_contacts').insert({
                case_id: newCase.id,
                contact_id: ic.contact_id,
                role: ic.immigration_role || 'applicant',
                is_primary: ic.immigration_role === 'applicant',
                is_payer: ic.is_payer || false,
                is_point_of_contact: ic.is_point_of_contact || false,
                dependent_of: ic.dependent_of,
                is_assigned: true
              });
            }
          } else if (selectedDeal?.contact?.id) {
            // Fallback: if no item_contacts, use deal contact
            await supabase.from('case_contacts').insert({
              case_id: newCase.id,
              contact_id: selectedDeal.contact.id,
              role: 'applicant',
              is_primary: true,
              is_payer: true
            });
          }
        } else if (selectedDeal?.contact?.id) {
          // No item selected, use deal contact
          await supabase.from('case_contacts').insert({
            case_id: newCase.id,
            contact_id: selectedDeal.contact.id,
            role: 'applicant',
            is_primary: true,
            is_payer: true
          });
        }

        toast.success('Case criado com sucesso!');
        onSuccess?.(newCase);
        handleClose();

      } else {
        // Mode B: Standalone case (for import)
        if (!selectedCatalogId) {
          toast.error('Selecione o tipo de case');
          setIsProcessing(false);
          return;
        }

        // Create case without item_id (standalone)
        const { data: newCase, error: caseError } = await supabase
          .from('cases')
          .insert({
            item_id: null, // Standalone case
            status: status,
            priority: priority,
            visa_type: selectedCatalog?.name || 'Unknown'
          })
          .select()
          .single();

        if (caseError) throw caseError;

        // Add selected contact if any
        if (selectedContact) {
          await supabase.from('case_contacts').insert({
            case_id: newCase.id,
            contact_id: selectedContact.id,
            role: 'applicant',
            is_primary: true,
            is_payer: true
          });
        }

        toast.success('Case criado com sucesso!');
        onSuccess?.(newCase);
        handleClose();
      }
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error(`Erro ao criar case: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setCreationMode('deal');
    setCurrentStep(1);
    // Deal mode states
    setContactSearchForDeal("");
    setContactsForDeal([]);
    setSelectedContactForDeal(null);
    setContactDeals([]);
    setSelectedDeal(null);
    setDealItems([]);
    setSelectedItem(null);
    // Standalone mode states
    setSelectedCatalogId("");
    setContactSearch("");
    setContacts([]);
    setSelectedContact(null);
    // Common states
    setStatus("preparation");
    setPriority("medium");
    setIsProcessing(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getContactName = (contact) => {
    if (!contact) return "Sem contato";
    return [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() || "Sem nome";
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return true; // Mode selection always valid
    }
    if (currentStep === 2) {
      if (creationMode === 'deal') {
        return !!selectedItem;
      } else {
        return !!selectedCatalogId;
      }
    }
    return false;
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Go to confirmation/submit
      handleSubmit();
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Novo Case
          </DialogTitle>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'
            }`}>
              1
            </div>
            <span className="text-sm font-medium">Modo</span>
          </div>
          <div className={`w-12 h-0.5 ${currentStep >= 2 ? 'bg-primary' : 'bg-muted'}`} />
          <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'
            }`}>
              2
            </div>
            <span className="text-sm font-medium">Detalhes</span>
          </div>
        </div>

        {/* Step 1: Choose creation mode */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <Label className="text-base font-medium">Como você quer criar este case?</Label>
            
            <RadioGroup 
              value={creationMode} 
              onValueChange={setCreationMode}
              className="grid gap-4"
            >
              <Label 
                htmlFor="mode-deal" 
                className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                  creationMode === 'deal' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value="deal" id="mode-deal" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium">
                    <Link2 className="h-4 w-4" />
                    Associar a um deal existente
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Vincule o case a um deal e item já existentes no sistema
                  </p>
                </div>
              </Label>

              <Label 
                htmlFor="mode-standalone" 
                className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                  creationMode === 'standalone' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value="standalone" id="mode-standalone" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium">
                    <FileText className="h-4 w-4" />
                    Criar case avulso
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Crie um case independente (útil para importação de dados)
                  </p>
                </div>
              </Label>
            </RadioGroup>
          </div>
        )}

        {/* Step 2: Details based on mode */}
        {currentStep === 2 && creationMode === 'deal' && (
          <div className="space-y-6">
            {/* Step 1: Contact Search */}
            <div className="space-y-2">
              <Label>1. Buscar Contato</Label>
              {!selectedContactForDeal ? (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Digite o nome do contato..."
                      value={contactSearchForDeal}
                      onChange={(e) => setContactSearchForDeal(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Contact Results */}
                  {isLoadingContactsForDeal && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {!isLoadingContactsForDeal && contactsForDeal.length > 0 && (
                    <div className="border rounded-lg max-h-48 overflow-y-auto">
                      {contactsForDeal.map((contact) => (
                        <button
                          key={contact.id}
                          onClick={() => {
                            setSelectedContactForDeal(contact);
                            setContactSearchForDeal("");
                            setContactsForDeal([]);
                          }}
                          className="w-full p-3 text-left hover:bg-muted/50 border-b last:border-b-0 transition-colors"
                        >
                          <div className="font-medium">{getContactName(contact)}</div>
                          <div className="text-sm text-muted-foreground">
                            {contact.primary_email || contact.primary_phone || 'Sem contato'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Card className="border-primary">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            {getContactName(selectedContactForDeal)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {selectedContactForDeal.primary_email || selectedContactForDeal.primary_phone}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedContactForDeal(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Step 2: Deal Selection (after contact is selected) */}
            {selectedContactForDeal && (
              <div className="space-y-2">
                <Label>2. Negociações de {getContactName(selectedContactForDeal)}</Label>
                
                {isLoadingContactDeals && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {!isLoadingContactDeals && contactDeals.length === 0 && (
                  <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                    Este contato não possui negociações
                  </p>
                )}

                {!isLoadingContactDeals && contactDeals.length > 0 && (
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {contactDeals.map((deal) => (
                      <button
                        key={deal.id}
                        onClick={() => setSelectedDeal(deal)}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          selectedDeal?.id === deal.id 
                            ? 'border-primary bg-primary/5' 
                            : 'hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{deal.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {deal.stage} • {deal.total_amount ? `R$ ${deal.total_amount.toLocaleString()}` : 'Sem valor'}
                            </div>
                          </div>
                          {selectedDeal?.id === deal.id && (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Item Selection (after deal is selected) */}
            {selectedDeal && (
              <div className="space-y-2">
                <Label>3. Selecionar Item (Tipo de Case)</Label>
                {dealItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                    Este deal não possui itens do tipo "case"
                  </p>
                ) : (
                  <div className="grid gap-2">
                    {dealItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedItem(item)}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          selectedItem?.id === item.id 
                            ? 'border-primary bg-primary/5' 
                            : 'hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{item.catalog?.name || 'Item'}</div>
                            <div className="text-sm text-muted-foreground">
                              Status: {item.status}
                            </div>
                          </div>
                          {selectedItem?.id === item.id && (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {currentStep === 2 && creationMode === 'standalone' && (
          <div className="space-y-6">
            {/* Case Type Selection */}
            <div className="space-y-2">
              <Label>Tipo de Case *</Label>
              <Select value={selectedCatalogId} onValueChange={setSelectedCatalogId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de case" />
                </SelectTrigger>
                <SelectContent>
                  {loadingCatalog ? (
                    <div className="p-2 text-center text-muted-foreground">
                      Carregando...
                    </div>
                  ) : caseTypes.length === 0 ? (
                    <div className="p-2 text-center text-muted-foreground">
                      Nenhum tipo de case encontrado
                    </div>
                  ) : (
                    caseTypes.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Contact Search */}
            <div className="space-y-2">
              <Label>Contato Principal (opcional)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar contato..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="pl-10"
                  disabled={!!selectedContact}
                />
              </div>

              {/* Contact Results */}
              {isLoadingContacts && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!isLoadingContacts && contacts.length > 0 && !selectedContact && (
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {contacts.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => {
                        setSelectedContact(contact);
                        setContactSearch("");
                        setContacts([]);
                      }}
                      className="w-full p-3 text-left hover:bg-muted/50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="font-medium">{getContactName(contact)}</div>
                      <div className="text-sm text-muted-foreground">
                        {contact.primary_email || contact.primary_phone || 'Sem contato'}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Contact */}
              {selectedContact && (
                <Card className="border-primary">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{getContactName(selectedContact)}</div>
                          <div className="text-sm text-muted-foreground">
                            {selectedContact.primary_email || selectedContact.primary_phone}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedContact(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Status and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preparation">Preparation</SelectItem>
                    <SelectItem value="filed">Filed</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="denied">Denied</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? handleClose : handlePrevStep}
          >
            {currentStep === 1 ? "Cancelar" : "Voltar"}
          </Button>

          <Button
            onClick={handleNextStep}
            disabled={isProcessing || !canProceed()}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Criando...
              </>
            ) : currentStep === 2 ? (
              "Criar Case"
            ) : (
              "Próximo"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
